<template>
  <div id="openlayers" class="mapViewer__map js-mapViewer__map"></div>
</template>

<script>
import { Map, View } from 'ol';
import { apply } from 'ol-mapbox-style';
import { Group as LayerGroup } from 'ol/layer';
import { fromLonLat, transformExtent } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Feature from 'ol/Feature';
import LineString from "ol/geom/LineString";
import 'ol/ol.css';

export default {
  name: "openlayers",
  props: ["items", "mapCommand", "selectedFerrySlug"],
  data() {
    return {
      layerGroup: null,
      markerLayer: null,
      markerSource: null,
      map: null,
      routeLayer: null,
      routeSource: null,
    };
  },
  mounted() {
    const netherlandsLonLat = [5.12142, 52.090737];
    const netherlandsWebMercator = fromLonLat(netherlandsLonLat);

    this.layerGroup = new LayerGroup();

    this.map = new Map({
      layers: [this.layerGroup],
      target: "openlayers",
      view: new View({
        center: netherlandsWebMercator,
        zoom: 10,
      }),
    });

    apply(this.layerGroup, '/app/themes/voetveren/assets/map-viewer/img/veerponten.json');

    this.initMarkers();
    this.initRoutes();
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        if (!newItems || !this.map) return;

        this.addMarkers(newItems);
        this.drawRoutes(newItems);
      }
    },
    mapCommand(cmd) {
      if (!cmd || !this.map) return;

      switch (cmd.type) {
        case "center":
          this.centerMap(cmd.payload.lng, cmd.payload.lat);

          break;

        case "fit-bounds":
          this.fitBounds(cmd.payload.extent);

          break;

        default:
          break;
      }
    },
    selectedFerrySlug(newSlug) {
      this.markerLayer.changed();
      this.centerOnFerry(newSlug);
    },
  },
  methods: {
    initMarkers() {
      this.markerSource = new VectorSource();
      this.markerLayer = new VectorLayer({
        source: this.markerSource,
        style: (feature) => {
          const ferrySlug = feature.get("ferrySlug");
          const isActive =
            !this.selectedFerrySlug ||
            ferrySlug === this.selectedFerrySlug;

          return new Style({
            image: new Icon({
              anchor: [0.5, 1],
              crossOrigin: "anonymous",
              src: isActive ?
                "/app/themes/voetveren/assets/map-viewer/img/marker.svg" :
                "/app/themes/voetveren/assets/map-viewer/img/marker-inactive.svg",
              width: 46,
              height: 46
            }),
            zIndex: isActive ? 100 : 1,
          });
        }
      });

      this.map.addLayer(this.markerLayer);

      // Cursor pointer on hover
      this.map.on("pointermove", (event) => {
        const hit = this.map.hasFeatureAtPixel(event.pixel, {
          layerFilter: (layer) => layer === this.markerLayer
        });

        this.map.getTargetElement().style.cursor = hit
          ? "pointer"
          : "";
      });

      // Change slug on click
      this.map.on("click", (event) => {
        this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
          const ferrySlug = feature.get("ferrySlug");

          if (ferrySlug) {
            this.$router.push({
              name: "Detail",
              params: {
                slug: ferrySlug,
              },
            })
          }
        });
      });

      // Get extent on map move
      this.map.on("moveend", () => {
        const extent = this.map.getView().calculateExtent(this.map.getSize());

        this.$emit("map-extent-change", extent);
      });
    },
    initRoutes() {
      this.routeSource = new VectorSource();
      this.routeLayer = new VectorLayer({
        source: this.routeSource,
      });

      this.map.addLayer(this.routeLayer);
    },
    addMarkers(items) {
      this.markerSource.clear();

      items.forEach((item) => {
        item.stops.forEach((stop) => {
          const marker = new Feature({
            geometry: new Point(
              fromLonLat([stop.lng, stop.lat])
            ),
            ferrySlug: item.slug,
            ferryStop: stop,
            ferryTitle: item.title,
          });

          this.markerSource.addFeature(marker);
        });
      });

      this.centerOnFerry(this.selectedFerrySlug);
    },
    drawRoutes(items) {
      this.routeSource.clear();

      items.map((item) => {
        const coordinates = item.stops.map(stop =>
          fromLonLat([stop.lng, stop.lat])
        );

        const route = new Feature({
          geometry: new LineString(coordinates)
        });

        this.routeSource.addFeature(route);
      });
    },
    centerOnFerry(slug) {
      if (slug) {
        const feature = this.markerSource
          .getFeatures()
          .find(f => f.get("ferrySlug") === slug);

        if (feature) {
          const ferryStop = feature.get("ferryStop");
          const ferryTitle = feature.get("ferryTitle");

          if (ferryStop) {
            this.centerMap(ferryStop.lng, ferryStop.lat);
          }

          if (ferryTitle) {
            document.title = "Veerponten - " + ferryTitle;
          }
        }
      } else {
        document.title = "Veerponten - Home";
      }
    },
    fitBounds(extent) {
      const [x1, y1, x2, y2] = extent;

      const normalizedExtent = [
        Math.min(x1, x2),
        Math.min(y1, y2),
        Math.max(x1, x2),
        Math.max(y1, y2)
      ];

      const transformedExtent = transformExtent(
        normalizedExtent,
        "EPSG:4326",
        "EPSG:3857"
      );

      this.map.getView().fit(transformedExtent, {
        padding: [50, 50, 50, 50],
        duration: 300,
        maxZoom: 16
      });
    },
    centerMap(lng, lat) {
      this.map.getView().animate({
        center: fromLonLat([lng, lat]),
        duration: 300,
      });
    }
  }
};
</script>

<style>
.ol-zoom {
  top: auto !important;
  bottom: .5em !important;
}
</style>